import React, { useContext, useCallback } from 'react';
import { observer } from 'mobx-react';
import {
  driverCarAssignmentStates,
  editableResidenceInsuranceType,
  isResidentialOwnerStates,
  standaloneRentersStates,
  policyType as PolicyType
} from '@ourbranch/lookups';
import { useFormikContext } from 'formik';

import { capitalize } from 'core/helpers/formatters';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import { AuthContext } from 'core/components/auth';
import FormList from 'core/components/form-list';
import AddDriver from 'common/components/people/add-form';
import CarAssignment from 'common/components/auto/car-assignment';
import { PersonalInjuryProtection } from 'common/components/personal-injury-protection';
import { useDisabledState } from 'common/disabled-context';
import { Coverage } from 'common/components/auto/auto-coverage';
import Cars from 'common/components/auto/cars';
import Trailers from 'common/components/auto/trailers';
import Renters from 'common/components/auto/renters';
import { ResidenceInsuranceType } from 'common/components/auto/residence-insurance-type';
import PriorCoverage from 'common/components/auto/prior-coverage';
import { BranchPlus } from 'common/components/branch-plus';
import { Disclaimer } from 'common/components/disclaimer';
import Discounts from 'customer/components/policy/discounts';
import { hasNewCoApplicant } from 'customer/components/policy/details/helpers';
import NonRatedIndividuals from 'common/components/people/non-rated-individuals';
import { PolicyDriver } from '../driver';

function AutoPolicy() {
  const session = useContext(AuthContext);
  const { values, initialValues, setFieldValue, setFieldTouched } = useFormikContext();
  const toast = useToast();

  const {
    account: {
      policies: {
        policy: { geographicState: state, policy, getAllowLicensedActions }
      }
    },
    account: {
      policies: { hasUmbrella }
    }
  } = useStore();
  const { disabled } = useDisabledState();

  const isBranchPlusAvailable = session?.canQuoteBranchPlus;

  const allowLicensedActions = getAllowLicensedActions(session);

  const newCoAppAdded = hasNewCoApplicant(initialValues?.drivers, values?.drivers);

  const checkForNonRatedIndividualsAndRemove = useCallback(
    (person) => {
      const firstName = person.firstName.trim().toUpperCase();
      const lastName = person.lastName.trim().toUpperCase();

      const personAlreadyAsNonRatedIndividual = values.removedDrivers.some(
        (driver) =>
          driver.firstName === firstName && driver.lastName === lastName && driver.dateOfBirth === person.dateOfBirth
      );
      if (personAlreadyAsNonRatedIndividual) {
        setFieldValue(
          'removedDrivers',
          values.removedDrivers.filter(
            (driver) =>
              driver.firstName !== firstName &&
              driver.lastName !== lastName &&
              driver.dateOfBirth !== person.dateOfBirth
          )
        );
        setFieldTouched('removedDrivers');
        toast.notify({
          type: 'success',
          message: `${capitalize(person.firstName)} ${capitalize(person.lastName)} was successfully added as a driver and removed as a non-rated individual.`
        });
      }
    },
    [values.removedDrivers, setFieldValue, setFieldTouched, toast]
  );

  return (
    <>
      <FormList
        disabled={disabled || !allowLicensedActions}
        id="drivers"
        item={PolicyDriver}
        addForm={AddDriver}
        addLabel="Add Driver"
        title="Drivers"
        singular="driver"
        plural="drivers"
        fromPolicy
        hasAuto
        onAdd={checkForNonRatedIndividualsAndRemove}
      />
      <NonRatedIndividuals disabled={disabled} />
      {newCoAppAdded && <Disclaimer />}
      <Cars disabled={disabled} fromPolicy />
      {driverCarAssignmentStates[state] && <CarAssignment state={state} disabled={disabled} />}
      <Trailers disabled={disabled} policy={policy} />
      {(editableResidenceInsuranceType.includes(state) || isResidentialOwnerStates.includes(state)) && (
        <ResidenceInsuranceType disabled={disabled} />
      )}
      <Coverage disabled={disabled} policy={policy} hasUmbrella={hasUmbrella} />
      <PriorCoverage />
      {!standaloneRentersStates[state] && <Renters disabled={disabled} />}
      {isBranchPlusAvailable && <BranchPlus fromPolicy policyType={PolicyType.Auto} />}
      <Discounts disabled={disabled} />
      {state === 'MI' && <PersonalInjuryProtection />}
    </>
  );
}

export default observer(AutoPolicy);
